import Decimal from "decimal.js";
import moment from "moment-timezone";
import { z } from "zod";
import { convertFormatMomentToLocalTimezone, convertFormatStringToLocalTimezone, formatDateWithoutTimezone, formatDateWithoutTimezoneToDate, safeParseMomentWithFormats } from "@/lib/api/date";
import { OtcFormValues } from "./types";
import { CommonDestinationTimezone } from "@/lib/utils";

/**
 * Explicit field-by-field transform from API/DB TradingContract shape
 * into the flat string/Date/number shape the form uses.
 *
 * Every field is listed.  No dynamic key-sniffing.
 */
export function contractToForm(contract: Record<string, unknown>): OtcFormValues {
  return {
    id: asString(contract.id),

    date: asDate(contract.date),
    end_date: asDate(contract.end_date),
    contract_type: asString(contract.contract_type),
    customer_id: asString(contract.customer_id),
    commodity: asString(contract.commodity),

    option_month: asString(contract.option_month),
    quantity: asString(contract.quantity),
    trade_reference: asString(contract.trade_reference),
    account_no_a: asString(contract.account_no_a),

    step_one: asString(contract.step_one),
    step_two: asString(contract.step_two),
    step_three: asString(contract.step_three),
    double_up: asString(contract.double_up),

    implied_fx_amount: asString(contract.implied_fx_amount),
    actual_contract_futures: asString(contract.actual_contract_futures),
    actual_contract_futures_step_one: asString(contract.actual_contract_futures_step_one),
    actual_contract_futures_step_two: asString(contract.actual_contract_futures_step_two),
    actual_contract_futures_double_up: asString(contract.actual_contract_futures_double_up),
    expected_contract_futures: asString(contract.expected_contract_futures),
    expected_contract_futures_step_one: asString(contract.expected_contract_futures_step_one),
    expected_contract_futures_step_two: asString(contract.expected_contract_futures_step_two),
    expected_contract_futures_double_up: asString(contract.expected_contract_futures_double_up),
    futures_margin: asString(contract.futures_margin),
    futures_margin_step_one: asString(contract.futures_margin_step_one),
    futures_margin_step_two: asString(contract.futures_margin_step_two),
    futures_margin_double_up: asString(contract.futures_margin_double_up),

    final_futures_price: asString(contract.final_futures_price),
    final_futures_expected_price: asString(contract.final_futures_expected_price),
    final_futures_price_date: asDate(contract.final_futures_price_date),
    final_futures_price_margin: asString(contract.final_futures_price_margin),

    account_no_b: asString(contract.account_no_b),
    futures_price: asString(contract.futures_price),
    otc_futures: asString(contract.otc_futures),

    option_premium: asString(contract.option_premium),
    contract_fee: asString(contract.contract_fee),
    contract_no: asString(contract.contract_no),
    contract_reference: asString(contract.contract_reference),

    adj_option_premium: asString(contract.adj_option_premium),
    adj_contract_fee: asString(contract.adj_contract_fee),
    last_adjusted_date: asDate(contract.last_adjusted_date),

    adj_option_premium_two: asString(contract.adj_option_premium_two),
    adj_contract_fee_two: asString(contract.adj_contract_fee_two),
    adjusted_date_two: asDate(contract.adjusted_date_two),

    adj_option_premium_three: asString(contract.adj_option_premium_three),
    adj_contract_fee_three: asString(contract.adj_contract_fee_three),
    adjusted_date_three: asDate(contract.adjusted_date_three),

    adj_option_premium_four: asString(contract.adj_option_premium_four),
    adj_contract_fee_four: asString(contract.adj_contract_fee_four),
    adjusted_date_four: asDate(contract.adjusted_date_four),

    adj_option_premium_five: asString(contract.adj_option_premium_five),
    adj_contract_fee_five: asString(contract.adj_contract_fee_five),
    adjusted_date_five: asDate(contract.adjusted_date_five),

    start_date: asDate(contract.start_date),
    weeks: asString(contract.weeks),
    period_qty: asString(contract.period_qty),
    knockout: asString(contract.knockout),

    shipment_start_date: asDate(contract.shipment_start_date),
    shipment_end_date: asDate(contract.shipment_end_date),
    trader_id: asString(contract.trader_id),
    crop_year: asInt(contract.crop_year),

    rolled_option_month: asString(contract.rolled_option_month),
    carry: asString(contract.carry),
    rolled_basis_month: asString(contract.rolled_basis_month),
    basis_carry: asString(contract.basis_carry),
    notes: asString(contract.notes),
    option_month_etc_one: asString(contract.option_month_etc_one),

    has_invoice_fees: asBoolean(contract.has_invoice_fees),
    has_contract_adjustment: asBoolean(contract.has_contract_adjustment),
  };
}

/**
 * Explicit field-by-field transform from form values to the shape the
 * API expects.
 *
 * When `activeValidator` is provided, the output is run through
 * `validator.parse()` which strips any fields not in the schema.
 * This is how fields like `step_one` get dropped for contract types
 * that don't have them, and how `implied_fx_amount` gets dropped
 * for non-canola commodities.
 */
export function formToApi(
  form: OtcFormValues,
  activeValidator?: z.ZodTypeAny,
): Record<string, unknown> {
  const full: Record<string, unknown> = {};

  // String pass-through
  full.id = form.id || undefined;
  full.contract_type = form.contract_type || undefined;
  full.commodity = form.commodity ? form.commodity.toLocaleUpperCase() : undefined;
  full.customer_id = form.customer_id || undefined;
  full.account_no_a = form.account_no_a || undefined;
  full.account_no_b = form.account_no_b || undefined;
  full.option_month = form.option_month || undefined;
  full.trade_reference = form.trade_reference || undefined;
  full.contract_no = form.contract_no || undefined;
  full.contract_reference = form.contract_reference || undefined;
  full.trader_id = form.trader_id || undefined;
  full.notes = form.notes || undefined;
  full.rolled_option_month = form.rolled_option_month || undefined;
  full.rolled_basis_month = form.rolled_basis_month || undefined;
  full.option_month_etc_one = form.option_month_etc_one || undefined;

  // Strings that represent numbers
  full.quantity = emptyToUndefined(form.quantity);
  full.step_one = emptyToUndefined(form.step_one);
  full.step_two = emptyToUndefined(form.step_two);
  full.step_three = emptyToUndefined(form.step_three);
  full.double_up = emptyToUndefined(form.double_up);
  full.option_premium = emptyToUndefined(form.option_premium);
  full.contract_fee = emptyToUndefined(form.contract_fee);
  full.futures_price = emptyToUndefined(form.futures_price);
  full.otc_futures = emptyToUndefined(form.otc_futures);
  full.implied_fx_amount = emptyToUndefined(form.implied_fx_amount);
  full.actual_contract_futures = emptyToUndefined(form.actual_contract_futures);
  full.actual_contract_futures_step_one = emptyToUndefined(form.actual_contract_futures_step_one);
  full.actual_contract_futures_step_two = emptyToUndefined(form.actual_contract_futures_step_two);
  full.actual_contract_futures_double_up = emptyToUndefined(form.actual_contract_futures_double_up);
  full.final_futures_price = emptyToUndefined(form.final_futures_price);
  full.final_futures_expected_price = emptyToUndefined(form.final_futures_expected_price);
  full.weeks = emptyToUndefined(form.weeks);
  full.period_qty = emptyToUndefined(form.period_qty);
  full.knockout = emptyToUndefined(form.knockout);
  full.carry = emptyToUndefined(form.carry);
  full.basis_carry = emptyToUndefined(form.basis_carry);
  full.adj_option_premium = emptyToUndefined(form.adj_option_premium);
  full.adj_contract_fee = emptyToUndefined(form.adj_contract_fee);
  full.adj_option_premium_two = emptyToUndefined(form.adj_option_premium_two);
  full.adj_contract_fee_two = emptyToUndefined(form.adj_contract_fee_two);
  full.adj_option_premium_three = emptyToUndefined(form.adj_option_premium_three);
  full.adj_contract_fee_three = emptyToUndefined(form.adj_contract_fee_three);
  full.adj_option_premium_four = emptyToUndefined(form.adj_option_premium_four);
  full.adj_contract_fee_four = emptyToUndefined(form.adj_contract_fee_four);
  full.adj_option_premium_five = emptyToUndefined(form.adj_option_premium_five);
  full.adj_contract_fee_five = emptyToUndefined(form.adj_contract_fee_five);

  // Derived / computed (read from form.values which has derives applied)
  full.margin = emptyToUndefined(form.margin as string | undefined);
  full.margin_amount = emptyToUndefined(form.margin_amount as string | undefined);
  full.expected_contract_futures = emptyToUndefined(form.expected_contract_futures as string | undefined);
  full.futures_margin = emptyToUndefined(form.futures_margin as string | undefined);
  full.expected_contract_futures_step_one = emptyToUndefined(form.expected_contract_futures_step_one as string | undefined);
  full.expected_contract_futures_step_two = emptyToUndefined(form.expected_contract_futures_step_two as string | undefined);
  full.expected_contract_futures_double_up = emptyToUndefined(form.expected_contract_futures_double_up as string | undefined);
  full.futures_margin_step_one = emptyToUndefined(form.futures_margin_step_one as string | undefined);
  full.futures_margin_step_two = emptyToUndefined(form.futures_margin_step_two as string | undefined);
  full.futures_margin_double_up = emptyToUndefined(form.futures_margin_double_up as string | undefined);
  full.final_futures_price_margin = emptyToUndefined(form.final_futures_price_margin as string | undefined);
  full.adj_margin = emptyToUndefined(form.adj_margin as string | undefined);
  full.adj_margin_amount = emptyToUndefined(form.adj_margin_amount as string | undefined);
  full.adj_margin_two = emptyToUndefined(form.adj_margin_two as string | undefined);
  full.adj_margin_amount_two = emptyToUndefined(form.adj_margin_amount_two as string | undefined);
  full.adj_margin_three = emptyToUndefined(form.adj_margin_three as string | undefined);
  full.adj_margin_amount_three = emptyToUndefined(form.adj_margin_amount_three as string | undefined);
  full.adj_margin_four = emptyToUndefined(form.adj_margin_four as string | undefined);
  full.adj_margin_amount_four = emptyToUndefined(form.adj_margin_amount_four as string | undefined);
  full.adj_margin_five = emptyToUndefined(form.adj_margin_five as string | undefined);
  full.adj_margin_amount_five = emptyToUndefined(form.adj_margin_amount_five as string | undefined);

  // crop_year is z.number() in the schema
  full.crop_year = form.crop_year;

  // Booleans
  full.has_invoice_fees = form.has_invoice_fees ?? false;
  full.has_contract_adjustment = form.has_contract_adjustment ?? false;

  // Dates → serialized to strings for the API
  full.date = dateToApiString(form.date);
  full.end_date = dateToApiString(form.end_date);
  full.start_date = dateToApiString(form.start_date);
  full.shipment_start_date = dateToApiString(form.shipment_start_date);
  full.shipment_end_date = dateToApiString(form.shipment_end_date);
  full.last_adjusted_date = dateToApiString(form.last_adjusted_date);
  full.adjusted_date_two = dateToApiString(form.adjusted_date_two);
  full.adjusted_date_three = dateToApiString(form.adjusted_date_three);
  full.adjusted_date_four = dateToApiString(form.adjusted_date_four);
  full.adjusted_date_five = dateToApiString(form.adjusted_date_five);
  full.final_futures_price_date = dateToApiString(form.final_futures_price_date);

  // Use the validator to strip fields not in the active schema
  if (activeValidator) {
    const result = activeValidator.safeParse(full);
    if (result.success) {
      return result.data as Record<string, unknown>;
    }
  }

  return full;
}

// ---------------------------------------------------------------------------
// Conversion helpers
// ---------------------------------------------------------------------------

function asBoolean(val: unknown): boolean | undefined {
  if (val == null) return undefined;
  if (typeof val === "boolean") return val;
  return undefined;
}

function asString(val: unknown): string | undefined {
  if (val == null) return undefined;
  if (val instanceof Decimal) return val.toString();
  if (typeof val === "number") return String(val);
  if (typeof val === "string") {
    return val === "" ? undefined : val;
  }
  return String(val);
}

function asInt(val: unknown): number | undefined {
  if (val == null) return undefined;
  if (typeof val === "number") return val;
  if (typeof val === "string") {
    const n = parseInt(val, 10);
    return isNaN(n) ? undefined : n;
  }
  return undefined;
}

function asDate(val: unknown): Date | undefined {
  if (val == null) return undefined;
  if (val instanceof Date) return formatDateWithoutTimezoneToDate(val);
  if (typeof val === "string" && val !== "") {
    const formats = ["YYYY-MM-DD", "YYYY/MM/DD", "YYYY-MM-DDTHH:mm:ss", moment.ISO_8601].reverse();
    const parsedFormat = safeParseMomentWithFormats(val, formats, CommonDestinationTimezone);
    
    return !!parsedFormat ? convertFormatMomentToLocalTimezone(parsedFormat) : formatDateWithoutTimezoneToDate(new Date(val));
  }
  return undefined;
}

function dateToApiString(val: Date | undefined): string | undefined {
  if (!val) return undefined;
  return moment.tz(formatDateWithoutTimezone(val), "YYYY/MM/DD", CommonDestinationTimezone).toISOString();
}

function emptyToUndefined(val: string | number | undefined): string | undefined {
  if (val == null) return undefined;
  const s = String(val);
  return s === "" ? undefined : s;
}
